import React from "react";
import "../../assets/fa-icons/css/fontawesome.css";
import "../../assets/fa-icons/css/sharp-regular.css";
import { FontStyles } from "../utils/font-styles.ts";
import { SectionHeader } from "../components/section-header.tsx";
import { cn } from "../utils/cn.ts";
import { InfoPopover } from "../components/base/popover.tsx";
import { usePropertyDetailsContext } from "./contexts/property-details-context.tsx";
import type { PropertyDetailsContextValue } from "./contexts/property-details-context.tsx";

const BROADBAND_THRESHOLDS = {
  STANDARD_MAX: 30,
  SUPERFAST_MAX: 300,
} as const;

const BROADBAND_ICONS = {
  STANDARD: "fa-regular fa-sharp fa-gauge-simple-low text-legacy-autumn pl-2",
  SUPERFAST: "fa-regular fa-sharp fa-gauge-simple text-glen-700 pl-2",
  ULTRAFAST: "fa-regular fa-sharp fa-gauge-simple-high text-glen-700 pl-2",
} as const;

interface BroadbandInfo {
  type: string;
  icon: string;
}

export const SignalIcon: React.FC<{ signalStrength: number }> = ({ signalStrength }) => {
  let icon;
  switch (signalStrength) {
    case 1:
      icon = "fa-signal-1 text-brick-600";
      break;
    case 2:
      icon = "fa-signal-2 text-legacy-autumn";
      break;
    case 3:
      icon = "fa-signal-3 text-legacy-autumn";
      break;
    case 4:
      icon = "fa-signal-4 text-glen-700";
      break;
    default:
      icon = "fa-signal-5 text-glen-700";
      break;
  }

  return (
    <span
      className="fa-stack !leading-[1.2] !w-3 !h-auto"
      aria-label={`Signal strength: ${signalStrength.toString()} out of 5`}
      role="img"
    >
      <i className="w-5 h-5 fa-solid fa-sharp fa-stack-1x fa-signal text-neutral-100"></i>
      <i className={cn("w-5 h-5 fa-solid fa-sharp fa-stack-1x", icon)}></i>
    </span>
  );
};

function getBroadbandInfo(maxSpeed: number | null | undefined): BroadbandInfo | null {
  if (maxSpeed == null || maxSpeed === 0) return null;

  if (maxSpeed < BROADBAND_THRESHOLDS.STANDARD_MAX) {
    return { type: "Standard", icon: BROADBAND_ICONS.STANDARD };
  } else if (maxSpeed < BROADBAND_THRESHOLDS.SUPERFAST_MAX) {
    return { type: "Superfast", icon: BROADBAND_ICONS.SUPERFAST };
  } else {
    return { type: "Ultrafast", icon: BROADBAND_ICONS.ULTRAFAST };
  }
}

export function hasUtilities(ctx: PropertyDetailsContextValue): boolean {
  const hasMobileSignal = ctx.mobileSignalCoverage != null && ctx.mobileSignalCoverage.length > 0;
  const hasServiceCharge = ctx.serviceCharge != null && ctx.serviceCharge > 0;
  const hasGroundRent = ctx.groundRent != null && ctx.groundRent > 0;
  const hasSharedOwnership = ctx.sharedOwnership != null && ctx.sharedOwnership > 0;
  return (
    hasMobileSignal || getBroadbandInfo(ctx.maxSpeed) != null || hasServiceCharge || hasGroundRent || hasSharedOwnership
  );
}

export const Utilities: React.FC = () => {
  const ctx = usePropertyDetailsContext();
  const { mobileSignalCoverage, maxSpeed, serviceCharge, groundRent, sharedOwnership } = ctx;
  if (!hasUtilities(ctx)) return null;

  const hasMobileSignal = mobileSignalCoverage != null && mobileSignalCoverage.length > 0;
  const broadbandInfo = getBroadbandInfo(maxSpeed);
  const hasServiceCharge = serviceCharge != null && serviceCharge > 0;
  const hasGroundRent = groundRent != null && groundRent > 0;
  const hasSharedOwnership = sharedOwnership != null && sharedOwnership > 0;

  return (
    <div className="flex flex-col gap-3 md:gap-3.5">
      <SectionHeader text="Utilities" as="h2" className={FontStyles.h5} />

      {hasMobileSignal && (
        <div className={cn("flex flex-wrap md:flex-row gap-x-8 gap-y-1", FontStyles["body-l"])}>
          <span className="flex flex-nowrap whitespace-nowrap">
            <span className="font-semibold">Mobile signal&nbsp;</span>(calls/texts only):
          </span>
          <div className="flex gap-x-6 md:gap-8">
            {mobileSignalCoverage.map((coverage) => (
              <div key={coverage.provider} className="flex gap-x-2">
                {coverage.provider}
                <SignalIcon signalStrength={coverage.signalStrength} />
              </div>
            ))}
          </div>
        </div>
      )}

      {broadbandInfo && (
        <div className={FontStyles["body-l"]}>
          <span className="font-semibold">{broadbandInfo.type} broadband</span> available with download speeds of up to{" "}
          <span className="font-semibold">{maxSpeed} Mbps</span> <i className={broadbandInfo.icon} />
        </div>
      )}

      {/* Additional Charges Section */}
      {(hasServiceCharge || hasGroundRent || hasSharedOwnership) && (
        <ul
          className={cn(
            "flex w-fit flex-wrap gap-4 xss:gap-7 md:gap-10",
            "list-inside list-disc marker:text-base",
            FontStyles["body-l"],
          )}
        >
          {hasServiceCharge && (
            <li className="text-nowrap">
              Service charge £{serviceCharge} p/a{" "}
              <InfoPopover
                ariaLabel="More information about Service charge"
                title="More information about Service charge"
                showArrow={false}
              >
                This property is subject to a Service Charge — commonly known in Scotland as a Factors Fee or
                Maintenance Fee. This regular payment (typically billed monthly, quarterly, or annually) covers your
                share of communal upkeep managed by a professional Property Factor. It generally includes costs for
                stair cleaning, landscaping, shared repairs, and buildings insurance, ensuring that communal areas are
                professionally maintained.
              </InfoPopover>
            </li>
          )}
          {hasGroundRent && <li className="text-nowrap">Ground rent £{groundRent} p/a</li>}
          {hasSharedOwnership && <li className="text-nowrap">Shared ownership {sharedOwnership}%</li>}
        </ul>
      )}
    </div>
  );
};
